'use client';
import React, { Suspense, useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getRoleDestination } from '@/lib/auth/roles';
import { getLoginErrorMessage, getLoginQueryErrorMessage } from '@/lib/auth/errors';
import { Eye, EyeOff, TrendingUp, TrendingDown } from 'lucide-react';
import Image from 'next/image';

const MARKET_TICKERS = [
  { symbol: 'AUD/USD', price: '0.6542', change: '+0.0023', pct: '+0.35%', up: true },
  { symbol: 'EUR/USD', price: '1.0891', change: '+0.0041', pct: '+0.38%', up: true },
  { symbol: 'GBP/USD', price: '1.2734', change: '-0.0018', pct: '-0.14%', up: false },
  { symbol: 'USD/JPY', price: '149.82', change: '+0.54', pct: '+0.36%', up: true },
  { symbol: 'XAU/USD', price: '2341.50', change: '+12.30', pct: '+0.53%', up: true },
  { symbol: 'BTC/USD', price: '67,420', change: '-320', pct: '-0.47%', up: false },
  { symbol: 'NAS100', price: '18,742', change: '+124', pct: '+0.67%', up: true },
  { symbol: 'SPX500', price: '5,312', change: '+18', pct: '+0.34%', up: true },
  { symbol: 'USD/CAD', price: '1.3621', change: '-0.0012', pct: '-0.09%', up: false },
  { symbol: 'ETH/USD', price: '3,521', change: '+45', pct: '+1.29%', up: true },
];

interface Candle {
  x: number;
  open: number;
  close: number;
  high: number;
  low: number;
  up: boolean;
}

function generateCandles(count: number, basePrice: number): Candle[] {
  const candles: Candle[] = [];
  let price = basePrice;
  for (let i = 0; i < count; i++) {
    const change = (Math.random() - 0.48) * 8;
    const open = price;
    const close = price + change;
    const high = Math.max(open, close) + Math.random() * 4;
    const low = Math.min(open, close) - Math.random() * 4;
    candles.push({ x: i, open, close, high, low, up: close >= open });
    price = close;
  }
  return candles;
}

function CandlestickChart({ width, height, color }: { width: number; height: number; color: string }) {
  const [candles, setCandles] = useState<Candle[]>([]);
  const baseRef = useRef(100);

  useEffect(() => {
    setCandles(generateCandles(40, baseRef.current));
    const interval = setInterval(() => {
      setCandles(prev => {
        const last = prev[prev.length - 1];
        const newPrice = last ? last.close : baseRef.current;
        const change = (Math.random() - 0.48) * 8;
        const open = newPrice;
        const close = newPrice + change;
        const high = Math.max(open, close) + Math.random() * 4;
        const low = Math.min(open, close) - Math.random() * 4;
        const newCandle: Candle = { x: prev.length, open, close, high, low, up: close >= open };
        return [...prev.slice(-39), newCandle];
      });
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  if (!candles.length) return null;

  const allPrices = candles.flatMap(c => [c.high, c.low]);
  const minP = Math.min(...allPrices);
  const maxP = Math.max(...allPrices);
  const range = maxP - minP || 1;
  const pad = 8;
  const chartH = height - pad * 2;
  const candleW = (width / candles.length) * 0.6;
  const gap = width / candles.length;

  const toY = (p: number) => pad + chartH - ((p - minP) / range) * chartH;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {candles.map((c, i) => {
        const cx = i * gap + gap / 2;
        const bodyTop = toY(Math.max(c.open, c.close));
        const bodyBot = toY(Math.min(c.open, c.close));
        const bodyH = Math.max(bodyBot - bodyTop, 1);
        const wickColor = c.up ? '#22c55e' : '#ef4444';
        return (
          <g key={i}>
            <line x1={cx} y1={toY(c.high)} x2={cx} y2={toY(c.low)} stroke={wickColor} strokeWidth="1" strokeOpacity="0.7" />
            <rect x={cx - candleW / 2} y={bodyTop} width={candleW} height={bodyH} fill={wickColor} fillOpacity={c.up ? '0.85' : '0.75'} rx="1" />
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedTicker() {
  const [offset, setOffset] = useState(0);
  const tickerRef = useRef<number>(0);

  useEffect(() => {
    let raf: number;
    const animate = () => {
      tickerRef.current -= 0.5;
      setOffset(tickerRef.current);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  const items = [...MARKET_TICKERS, ...MARKET_TICKERS];
  const itemWidth = 160;
  const totalWidth = MARKET_TICKERS.length * itemWidth;

  const displayOffset = ((tickerRef.current % totalWidth) - totalWidth) % totalWidth;

  return (
    <div className="overflow-hidden w-full">
      <div
        className="flex gap-0 whitespace-nowrap"
        style={{ transform: `translateX(${displayOffset}px)`, willChange: 'transform' }}
      >
        {items.map((t, i) => (
          <div key={i} className="flex items-center gap-2 px-4 py-1.5 border-r border-white/10" style={{ minWidth: itemWidth }}>
            <span className="text-xs font-bold text-white/80">{t.symbol}</span>
            <span className="text-xs text-white/60">{t.price}</span>
            <span className={`text-xs font-semibold ${t.up ? 'text-green-400' : 'text-red-400'}`}>{t.pct}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LivePriceCard({ ticker }: { ticker: typeof MARKET_TICKERS[0] }) {
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFlash(true);
      setTimeout(() => setFlash(false), 400);
    }, 2000 + Math.random() * 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={`rounded-xl border p-3 transition-all duration-300 ${flash ? 'border-green-400/40 bg-green-400/5' : 'border-white/10 bg-white/5'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold text-white/90">{ticker.symbol}</span>
        {ticker.up ? <TrendingUp size={12} className="text-green-400" /> : <TrendingDown size={12} className="text-red-400" />}
      </div>
      <div className="text-sm font-bold text-white">{ticker.price}</div>
      <div className={`text-xs font-semibold ${ticker.up ? 'text-green-400' : 'text-red-400'}`}>{ticker.pct}</div>
    </div>
  );
}

function LoginForm() {
  const { signIn } = useAuth();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const msg = getLoginQueryErrorMessage(searchParams.get('error'));
    if (msg) setError(msg);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSigningIn(true);
    try {
      if (!email.trim() || !password) {
        setError('Please enter both email and password.');
        setSigningIn(false);
        return;
      }

      // signIn establishes the session cookie, then returns profile/role.
      const data = await signIn(email.trim(), password);

      const role =
        data?.profile?.role ??
        data?.user?.user_metadata?.role ??
        'client';

      // Hard navigate to the role portal so cookies are sent with the next
      // request. Avoid absolute SITE_URL redirects (cross-host cookie loss).
      window.location.assign(getRoleDestination(role));
    } catch (err: unknown) {
      setError(getLoginErrorMessage(err));
      setSigningIn(false);
    }
  };

  const featuredTickers = MARKET_TICKERS.slice(0, 6);

  return (
    <div className="min-h-screen flex overflow-hidden bg-[#080c14]">
      {/* LEFT PANEL — Market Visual */}
      <div className="hidden lg:flex lg:w-[55%] relative flex-col overflow-hidden">
        {/* Deep dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#080c14] via-[#0d1520] to-[#060a10]" />

        {/* Animated glow orbs */}
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-cyan-500/8 blur-[120px] animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-blue-600/8 blur-[100px] animate-pulse" style={{ animationDelay: '1.5s' }} />
        <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full bg-emerald-500/5 blur-[80px] animate-pulse" style={{ animationDelay: '3s' }} />

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(0,200,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />

        <div className="relative z-10 flex flex-col h-full p-8">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div
              className="relative flex-shrink-0"
              style={{
                width: 64,
                height: 64,
                filter: 'drop-shadow(0 0 12px rgba(99,102,241,0.7)) drop-shadow(0 0 24px rgba(139,92,246,0.5))',
              }}
            >
              <Image
                src="/assets/images/Blue_and_Purple_Modern_Travel_Agency_Logo__2_-removebg-preview-1788006833584.png"
                alt="Capitals.Au Logo"
                fill
                className="object-contain"
              />
            </div>
            <div className="flex flex-col leading-none">
              <span
                style={{
                  fontFamily: "'Montserrat', 'Inter', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.6rem',
                  letterSpacing: '0.05em',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #3b82f6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.1,
                }}
              >
                CAPITALS
              </span>
              <span
                style={{
                  fontFamily: "'Montserrat', 'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: '1rem',
                  letterSpacing: '0.08em',
                  color: '#94a3b8',
                  lineHeight: 1.2,
                }}
              >
                .au
              </span>
            </div>
          </div>

          {/* Main headline */}
          <div className="mb-8">
            <h2 className="text-4xl font-bold text-white leading-tight mb-3">
              Institutional-Grade<br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Trading Intelligence</span>
            </h2>
            <p className="text-white/50 text-sm leading-relaxed max-w-sm">
              Real-time portfolio analytics, multi-tier account management, and live market data — all in one platform.
            </p>
          </div>

          {/* Live price cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {featuredTickers.map((ticker, i) => (
              <LivePriceCard key={i} ticker={ticker} />
            ))}
          </div>

          {/* Candlestick charts */}
          <div className="grid grid-cols-2 gap-4 mb-6 flex-1">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-white/80">EUR/USD</span>
                <span className="text-xs text-green-400 font-semibold">+0.38%</span>
              </div>
              <CandlestickChart width={200} height={80} color="#22c55e" />
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-white/80">BTC/USD</span>
                <span className="text-xs text-red-400 font-semibold">-0.47%</span>
              </div>
              <CandlestickChart width={200} height={80} color="#ef4444" />
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Active Traders', value: '12,847' },
              { label: 'Daily Volume', value: '$2.4B' },
              { label: 'Instruments', value: '500+' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-lg font-bold text-white">{stat.value}</div>
                <div className="text-xs text-white/40">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Ticker */}
          <div className="border-t border-white/10 pt-3">
            <AnimatedTicker />
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 relative bg-[#080c14]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#080c14] to-[#0a0f1a]" />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-blue-600/5 blur-[80px]" />

        <div className="relative z-10 w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div
              className="relative flex-shrink-0"
              style={{
                width: 52,
                height: 52,
                filter: 'drop-shadow(0 0 10px rgba(99,102,241,0.7)) drop-shadow(0 0 20px rgba(139,92,246,0.5))',
              }}
            >
              <Image
                src="/assets/images/Blue_and_Purple_Modern_Travel_Agency_Logo__2_-removebg-preview-1788006833584.png"
                alt="Capitals.Au Logo"
                fill
                className="object-contain"
              />
            </div>
            <div className="flex flex-col leading-none">
              <span
                style={{
                  fontFamily: "'Montserrat', 'Inter', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.3rem',
                  letterSpacing: '0.05em',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #3b82f6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.1,
                }}
              >
                CAPITALS
              </span>
              <span
                style={{
                  fontFamily: "'Montserrat', 'Inter', sans-serif",
                  fontWeight: 400,
                  fontSize: '0.85rem',
                  letterSpacing: '0.08em',
                  color: '#94a3b8',
                  lineHeight: 1.2,
                }}
              >
                .au
              </span>
            </div>
          </div>

          {/* Form card */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-1">Welcome back</h2>
              <p className="text-white/40 text-sm">Sign in to your trading account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-widest mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 text-sm focus:outline-none focus:border-cyan-500/50 focus:bg-white/8 transition-all"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-widest mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-white placeholder-white/20 text-sm focus:outline-none focus:border-cyan-500/50 focus:bg-white/8 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={signingIn}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-all duration-200 text-sm tracking-wide"
              >
                {signingIn ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Signing in…
                  </span>
                ) : 'Sign In'}
              </button>
            </form>

            {/* Footer note */}
            <p className="mt-6 text-center text-xs text-white/20">
              Secured by 256-bit encryption · Capitals.Au © 2026
            </p>
          </div>

          {/* Regulatory Frameworks */}
          <div className="mt-6 space-y-4">
            {/* Verified badge */}
            <div className="flex items-center justify-center gap-2">
              <div className="h-px flex-1 bg-white/10" />
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-green-400 flex-shrink-0">
                  <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-semibold text-green-400">Regulated &amp; Verified</span>
              </div>
              <div className="h-px flex-1 bg-white/10" />
            </div>

            {/* Regulatory cards */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { flag: '🇦🇺', acronym: 'ASIC', name: 'Australian Securities & Investments Commission', license: 'AFSL No. 000 000 000', color: 'border-cyan-500/20 bg-cyan-500/5' },
                { flag: '🇬🇧', acronym: 'FCA', name: 'Financial Conduct Authority', license: 'FRN: 982642', color: 'border-blue-500/20 bg-blue-500/5' },
                { flag: '🇨🇾', acronym: 'CySEC', name: 'Cyprus Securities & Exchange Commission', license: 'CIF License No. 000/00', color: 'border-emerald-500/20 bg-emerald-500/5' },
                { flag: '🇿🇦', acronym: 'FSCA', name: 'Financial Sector Conduct Authority', license: 'FSP No. 00000', color: 'border-amber-500/20 bg-amber-500/5' },
              ].map((reg) => (
                <div key={reg.acronym} className={`rounded-xl border ${reg.color} p-3 flex items-start gap-2`}>
                  <span className="text-base flex-shrink-0">{reg.flag}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-xs font-bold text-white/90">{reg.acronym}</span>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="text-green-400 flex-shrink-0">
                        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <p className="text-[10px] text-white/40 leading-tight truncate">{reg.license}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Protection badges */}
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                { icon: '🔒', label: 'Segregated Funds' },
                { icon: '🛡️', label: 'Negative Balance Protection' },
                { icon: '✅', label: 'AML/KYC Compliant' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                  <span className="text-xs">{item.icon}</span>
                  <span className="text-[10px] text-white/50 font-medium">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Risk warning */}
            <p className="text-[10px] text-white/20 text-center leading-relaxed">
              Trading CFDs involves significant risk. Losses can exceed deposits. Not suitable for all investors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-[#080c14] text-white/60 text-sm">
          Loading…
        </div>
      }
    >
      <LoginForm />
    </Suspense>
  );
}
